import React, {Component} from "react";
import SimpleReactValidator from "simple-react-validator";

/* Import Components */
import Input from "./Input";
import TextArea from "./TextArea";
import Select from "./Select";
import Button from "./Button";
import EmailInput from "./EmailInput";
import TelephoneInput from "./TelephoneInput";
import Confirmation from "./Confirmation";
import ResearchDisclaimer from "./ResearchDisclaimer";
import CheckBox from "./CheckBox";
import RadioButton from "./RadioButton";
import FileInput from "./FileInput";

import FetchHelper from "./helpers/FetchHelper";

class FormContainer extends Component {
    constructor(props) {
        super(props);

        this.state = {
            initialState: true,
            loading: false,
            submitted: false,
            newUser: {},
            documents: [],
            // populated from the API response when the server could not accept a file
            rejectedDocuments: [],
            localAuthorityOptions: props.localAuthorityOptions || [],
            schoolOptions: props.schoolOptions || [],
            referrerRoleOptions: props.referrerRoleOptions || [],
            genderOptions: props.genderOptions || [],
            schoolYearOptions: props.schoolYearOptions || [],
            parentRelationshipOptions: props.parentRelationshipOptions || [],
            existingDiagnosisOptions: props.existingDiagnosisOptions || [],
            nddPathwayOptions: props.nddPathwayOptions || [],
            focusAreaOptions: props.focusAreaOptions || [],
            riskStatusOptions: props.riskStatusOptions || [],
            yesNoUnknownOptions: ["Yes", "No", "Unknown"],
            yesNoOptions: ["Yes", "No"],
        };

        const validateNumber = (phone_number) => {
            if (!phone_number || phone_number?.length === 0) {
                return {
                    valid: true,
                    message: "phone number not required"
                }
            } else {
                if (phone_number?.startsWith('07') || phone_number?.startsWith('+44')) {
                    let numberLength = phone_number.length || 0
                    if (phone_number?.startsWith('0')) {
                        if (numberLength !== 11) {
                            return {
                                valid: false,
                                message: `UK phone number starting with (0) must be 11 characters, currently ${numberLength} chars.`
                            }
                        }
                    }
                    if (phone_number?.startsWith('+44')) {
                        if (numberLength !== 13) {
                            return {
                                valid: false,
                                message: `UK phone number starting with (+44) must be 13 characters, currently ${numberLength} chars`
                            }
                        }
                    }
                    if (!phone_number?.match(/^[+0-9]*$/)) {
                        return {
                            valid: false,
                            message: "A phone number can only have numbers or +"
                        }
                    }
                } else {
                    return {
                        valid: false,
                        message: `UK phone number must start with either (+44) or (07)`
                    }
                }
            }
            return {
                valid: true,
            }
        }
        this.validator = new SimpleReactValidator({
            autoForceUpdate: this,
            locale: "en",
            className: "text-danger",
            validators: {
                custom_phone_number: {
                    message: ':replaced',
                    rule: (val, params, validator) => {
                        return validateNumber(val).valid
                    },
                    messageReplace: (message, params) => {
                        return validateNumber(params[0]).message
                    },
                }
            }
        });
    }

    handleInputChange = e => {
        let value = e.target.value;
        let name = e.target.name;
        this.setState(prevState => ({
            newUser: {
                ...prevState.newUser,
                [name]: value
            }
        }));
    };

    // changing the local authority resets the dependent school selection
    handleLocalAuthorityChange = e => {
        const value = e.target.value;
        this.setState(prevState => ({
            newUser: {
                ...prevState.newUser,
                localAuthorityId: value,
                schoolId: ""
            }
        }));
    };

    handleCheckBox = e => {
        const {name, value} = e.target;
        const {newUser} = this.state;
        let values = newUser[name] || [];
        if (values.indexOf(value) > -1) {
            values = values.filter(s => s !== value);
        } else {
            values.push(value);
        }

        this.setState(prevState => ({
            newUser: {...prevState.newUser, [name]: values}
        }));
    };

    handleDocumentsChange = files => {
        this.setState({documents: files});
    };

    handleFormSubmit = e => {
        e.preventDefault();

        const {csrfToken, parentDomain} = this.props;
        let userData = this.state.newUser;

        this.setState({initialState: false});

        FetchHelper.fetchWithPost({
            context: this,
            csrfToken,
            parentDomain,
            type: "neuro_pathways",
            body: userData,
            files: this.state.documents
        });
    };

    renderSubmitButton = () => {
        const {initialState, loading} = this.state;
        const valid = initialState || this.validator.allValid();
        return (
            <>
                {!valid && (
                    <div className="alert alert-danger">
                        There are unresolved form errors, please correct the form before
                        resubmitting.
                    </div>
                )}
                <Button
                    type="submit"
                    className="btn-primary"
                    disabled={!valid || loading}
                >
                    Submit
                </Button>
            </>
        );
    };

    filteredSchoolOptions = () => {
        const {schoolOptions, newUser} = this.state;
        const authorityId = parseInt(this.state.newUser.localAuthorityId, 10);
        if (!authorityId) {
            return [];
        }
        return schoolOptions.filter(school => school.parent_id === authorityId);
    };

    render() {
        const {
            referrerName,
            referrerRole,
            referrerRoleOther,
            referrerOrganisation,
            referrerPhone,
            referrerEmail,
            dateOfReferral,
            firstName,
            lastName,
            dateOfBirth,
            gender,
            genderSelfDescribe,
            address1,
            address2,
            postcode,
            localAuthorityId,
            schoolId,
            schoolYear,
            parentCarerName,
            parentRelationship,
            parentRelationshipOther,
            parentalResponsibility,
            parentPhone,
            parentEmail,
            secondParentCarer,
            careExperienced,
            lookedAfter,
            existingDiagnosis,
            existingDiagnosisOther,
            nddPathway,
            nddPathwayOther,
            nddReferralDate,
            previousNdSupport,
            previousNdSupportDetails,
            referralReason,
            mainConcerns,
            focusAreas,
            focusAreaOther,
            riskStatus,
            riskDetails,
            childProtectionConcerns,
            traumaExperiences,
            traumaDetails,
            consentReferral,
            consentSchoolContact,
            consentAnonymisedReporting,
            childViewsSought,
            neuroPathwaysConfirmation,
        } = this.state.newUser;

        const {
            submitted,
            documents,
            rejectedDocuments,
            localAuthorityOptions,
            referrerRoleOptions,
            genderOptions,
            schoolYearOptions,
            parentRelationshipOptions,
            existingDiagnosisOptions,
            nddPathwayOptions,
            focusAreaOptions,
            riskStatusOptions,
            yesNoUnknownOptions,
            yesNoOptions,
        } = this.state;

        // drop validation for conditional fields that are no longer rendered
        this.validator.purgeFields();

        return (
            <div className="container">
                <h1>
                    NeuroPathways Clinic
                    <small>Prediagnostic support referral form - neurodevelopmental support for children, young people and families</small>
                </h1>
                <div className="row justify-content-start">
                    {submitted ? (
                        <>
                            {rejectedDocuments.length > 0 && (
                                <div className="col-12">
                                    <div className="alert alert-danger">
                                        <p>
                                            <strong>
                                                Your referral was submitted, but the following
                                                {rejectedDocuments.length === 1 ? " file " : " files "}
                                                could not be uploaded:
                                            </strong>
                                        </p>
                                        <ul>
                                            {rejectedDocuments.map((message, index) => (
                                                <li key={index}>{message}</li>
                                            ))}
                                        </ul>
                                        <p>
                                            Please contact the NeuroPathways team to send
                                            {rejectedDocuments.length === 1 ? " this document " : " these documents "}
                                            another way.
                                        </p>
                                    </div>
                                </div>
                            )}
                            <Confirmation
                                referralType="Neuro Pathways"
                                returnCallback={() => {
                                    this.setState({
                                        submitted: false,
                                        newUser: {},
                                        documents: [],
                                        rejectedDocuments: [],
                                        initialState: true
                                    });
                                }}
                            />
                        </>
                    ) : (
                        <form className="col-8" onSubmit={this.handleFormSubmit} noValidate>
                            <div className="alert alert-secondary">
                                <p>
                                    Fields marked with <span className="required-field">*</span>{" "}
                                    are mandatory.
                                </p>
                            </div>

                            <h2>1. Referrer details</h2>
                            <div className="form-group">
                                <Input
                                    inputtype={"text"}
                                    label="Name"
                                    name="referrerName"
                                    value={referrerName}
                                    required={true}
                                    onChange={this.handleInputChange}
                                />{" "}
                                {this.validator.message("referrer name", referrerName, "required|alpha_space")}
                            </div>
                            <div className="form-group">
                                <RadioButton
                                    label="Role"
                                    name="referrerRole"
                                    options={referrerRoleOptions}
                                    value={referrerRole}
                                    required={true}
                                    onChange={this.handleInputChange}
                                />
                                {this.validator.message("referrer role", referrerRole, "required")}
                            </div>
                            {referrerRole === "Other" && (
                                <div className="form-group">
                                    <Input
                                        inputtype={"text"}
                                        label="Please describe your role"
                                        name="referrerRoleOther"
                                        value={referrerRoleOther}
                                        required={true}
                                        onChange={this.handleInputChange}
                                    />{" "}
                                    {this.validator.message("referrer role description", referrerRoleOther, "required")}
                                </div>
                            )}
                            <div className="form-group">
                                <Input
                                    inputtype={"text"}
                                    label="Organisation or service"
                                    name="referrerOrganisation"
                                    value={referrerOrganisation}
                                    required={true}
                                    onChange={this.handleInputChange}
                                />{" "}
                                {this.validator.message("organisation or service", referrerOrganisation, "required")}
                            </div>
                            <div className="form-group">
                                <TelephoneInput
                                    name="referrerPhone"
                                    label={"Phone number"}
                                    value={referrerPhone}
                                    required={true}
                                    onChange={this.handleInputChange}
                                />{" "}
                                {this.validator.message(
                                    "referrer phone number",
                                    referrerPhone,
                                    `required|custom_phone_number:${referrerPhone}`
                                )}
                            </div>
                            <div className="form-group">
                                <EmailInput
                                    label={"Email address"}
                                    name="referrerEmail"
                                    id="referrerEmail"
                                    value={referrerEmail}
                                    required={true}
                                    onChange={this.handleInputChange}
                                />
                                {this.validator.message("referrer email address", referrerEmail, "required|email")}
                            </div>
                            <div className="form-group">
                                <Input
                                    name="dateOfReferral"
                                    label="Date of referral (DD/MM/YYYY)"
                                    value={dateOfReferral}
                                    required={true}
                                    type="date"
                                    onChange={this.handleInputChange}
                                />{" "}
                                {this.validator.message("date of referral", dateOfReferral, "required")}
                            </div>

                            <h2>2. About the child or young person</h2>
                            <div className="form-group">
                                <Input
                                    inputtype={"text"}
                                    label="First name"
                                    name="firstName"
                                    value={firstName}
                                    required={true}
                                    onChange={this.handleInputChange}
                                />{" "}
                                {this.validator.message("first name", firstName, "required|alpha_space")}
                            </div>
                            <div className="form-group">
                                <Input
                                    inputtype={"text"}
                                    label="Surname"
                                    name="lastName"
                                    value={lastName}
                                    required={true}
                                    onChange={this.handleInputChange}
                                />{" "}
                                {this.validator.message("surname", lastName, "required|alpha_space")}
                            </div>
                            <div className="form-group">
                                <Input
                                    name="dateOfBirth"
                                    label="Date of birth (DD/MM/YYYY)"
                                    value={dateOfBirth}
                                    required={true}
                                    type="date"
                                    onChange={this.handleInputChange}
                                />{" "}
                                {this.validator.message("date of birth", dateOfBirth, "required")}
                            </div>
                            <div className="form-group">
                                <RadioButton
                                    label="Gender"
                                    name="gender"
                                    options={genderOptions}
                                    value={gender}
                                    required={true}
                                    onChange={this.handleInputChange}
                                />
                                {this.validator.message("gender", gender, "required")}
                            </div>
                            {gender === "Prefer to self describe" && (
                                <div className="form-group">
                                    <Input
                                        inputtype={"text"}
                                        label="Please describe"
                                        name="genderSelfDescribe"
                                        value={genderSelfDescribe}
                                        required={true}
                                        onChange={this.handleInputChange}
                                    />{" "}
                                    {this.validator.message("gender description", genderSelfDescribe, "required")}
                                </div>
                            )}
                            <div className="form-group">
                                <Input
                                    inputtype={"text"}
                                    label="Address"
                                    name="address1"
                                    value={address1}
                                    required={true}
                                    onChange={this.handleInputChange}
                                />{" "}
                                {this.validator.message("address", address1, "required")}
                            </div>
                            <div className="form-group">
                                <Input
                                    inputtype={"text"}
                                    label="Address line 2"
                                    name="address2"
                                    value={address2}
                                    onChange={this.handleInputChange}
                                />
                            </div>
                            <div className="form-group">
                                <Input
                                    inputtype={"text"}
                                    label="Postcode"
                                    name="postcode"
                                    value={postcode}
                                    required={true}
                                    onChange={this.handleInputChange}
                                />{" "}
                                {this.validator.message("postcode", postcode, "required")}
                            </div>
                            <div className="form-group">
                                <Select
                                    label="Local authority area"
                                    name="localAuthorityId"
                                    options={localAuthorityOptions}
                                    value={localAuthorityId}
                                    placeholder={"Please Select"}
                                    handleChange={this.handleLocalAuthorityChange}
                                    required
                                />{" "}
                                {this.validator.message("local authority area", localAuthorityId, "required")}
                            </div>
                            <div className="form-group">
                                <Select
                                    label="School or nursery"
                                    name="schoolId"
                                    options={this.filteredSchoolOptions()}
                                    value={schoolId}
                                    placeholder={localAuthorityId ? "Please Select" : "Please select a local authority area first"}
                                    handleChange={this.handleInputChange}
                                    required
                                />{" "}
                                {this.validator.message("school or nursery", schoolId, "required")}
                            </div>
                            <div className="form-group">
                                <Select
                                    label="School year or stage"
                                    name="schoolYear"
                                    options={schoolYearOptions}
                                    value={schoolYear}
                                    placeholder={"Please Select"}
                                    handleChange={this.handleInputChange}
                                    required
                                />{" "}
                                {this.validator.message("school year or stage", schoolYear, "required")}
                            </div>

                            <h2>3. Parent or carer</h2>
                            <p>
                                If there is a second parent or carer who should be contacted, please add
                                their details in the space at the end of this section.
                            </p>
                            <div className="form-group">
                                <Input
                                    inputtype={"text"}
                                    label="Full name"
                                    name="parentCarerName"
                                    value={parentCarerName}
                                    required={true}
                                    onChange={this.handleInputChange}
                                />{" "}
                                {this.validator.message("parent or carer full name", parentCarerName, "required|alpha_space")}
                            </div>
                            <div className="form-group">
                                <RadioButton
                                    label="Relationship to child"
                                    name="parentRelationship"
                                    options={parentRelationshipOptions}
                                    value={parentRelationship}
                                    required={true}
                                    onChange={this.handleInputChange}
                                />
                                {this.validator.message("relationship to child", parentRelationship, "required")}
                            </div>
                            {parentRelationship === "Other" && (
                                <div className="form-group">
                                    <Input
                                        inputtype={"text"}
                                        label="Please describe the relationship"
                                        name="parentRelationshipOther"
                                        value={parentRelationshipOther}
                                        required={true}
                                        onChange={this.handleInputChange}
                                    />{" "}
                                    {this.validator.message("relationship description", parentRelationshipOther, "required")}
                                </div>
                            )}
                            <div className="form-group">
                                <RadioButton
                                    label="Do they hold parental responsibility?"
                                    name="parentalResponsibility"
                                    options={yesNoUnknownOptions}
                                    value={parentalResponsibility}
                                    required={true}
                                    onChange={this.handleInputChange}
                                />
                                {this.validator.message("parental responsibility", parentalResponsibility, "required")}
                            </div>
                            <div className="form-group">
                                <TelephoneInput
                                    name="parentPhone"
                                    label={"Main phone number"}
                                    value={parentPhone}
                                    required={true}
                                    onChange={this.handleInputChange}
                                />{" "}
                                {this.validator.message(
                                    "parent or carer phone number",
                                    parentPhone,
                                    `required|custom_phone_number:${parentPhone}`
                                )}
                            </div>
                            <div className="form-group">
                                <EmailInput
                                    label={"Email address"}
                                    name="parentEmail"
                                    id="parentEmail"
                                    value={parentEmail}
                                    onChange={this.handleInputChange}
                                />
                                {this.validator.message("parent or carer email address", parentEmail, "email")}
                            </div>
                            <div className="form-group">
                                <TextArea
                                    name="secondParentCarer"
                                    label="Second parent or carer (name, relationship and contact details)"
                                    value={secondParentCarer}
                                    maxLength={500}
                                    onChange={this.handleInputChange}
                                />
                            </div>

                            <h2>4. Family circumstances</h2>
                            <div className="form-group">
                                <RadioButton
                                    label="Is the child care experienced?"
                                    name="careExperienced"
                                    options={yesNoUnknownOptions}
                                    value={careExperienced}
                                    required={true}
                                    onChange={this.handleInputChange}
                                />
                                {this.validator.message("care experienced", careExperienced, "required")}
                            </div>
                            <div className="form-group">
                                <RadioButton
                                    label="Is the child looked after?"
                                    name="lookedAfter"
                                    options={yesNoUnknownOptions}
                                    value={lookedAfter}
                                    required={true}
                                    onChange={this.handleInputChange}
                                />
                                {this.validator.message("looked after", lookedAfter, "required")}
                            </div>

                            <h2>5. Neurodevelopmental pathway</h2>
                            <div className="form-group">
                                <RadioButton
                                    label="Does the child have an existing neurodevelopmental diagnosis?"
                                    name="existingDiagnosis"
                                    options={existingDiagnosisOptions}
                                    value={existingDiagnosis}
                                    required={true}
                                    onChange={this.handleInputChange}
                                />
                                {this.validator.message("existing diagnosis", existingDiagnosis, "required")}
                            </div>
                            {existingDiagnosis === "Yes, other" && (
                                <div className="form-group">
                                    <Input
                                        inputtype={"text"}
                                        label="Please give details of the diagnosis"
                                        name="existingDiagnosisOther"
                                        value={existingDiagnosisOther}
                                        required={true}
                                        onChange={this.handleInputChange}
                                    />{" "}
                                    {this.validator.message("diagnosis details", existingDiagnosisOther, "required")}
                                </div>
                            )}
                            <div className="form-group">
                                <RadioButton
                                    label="Which NDD pathway is the child currently on?"
                                    name="nddPathway"
                                    options={nddPathwayOptions}
                                    value={nddPathway}
                                    required={true}
                                    onChange={this.handleInputChange}
                                />
                                {this.validator.message("NDD pathway", nddPathway, "required")}
                            </div>
                            {nddPathway === "Other" && (
                                <div className="form-group">
                                    <Input
                                        inputtype={"text"}
                                        label="Please describe the pathway"
                                        name="nddPathwayOther"
                                        value={nddPathwayOther}
                                        required={true}
                                        onChange={this.handleInputChange}
                                    />{" "}
                                    {this.validator.message("pathway description", nddPathwayOther, "required")}
                                </div>
                            )}
                            <div className="form-group">
                                <Input
                                    name="nddReferralDate"
                                    label="Date referred to the NDD pathway (DD/MM/YYYY)"
                                    value={nddReferralDate}
                                    type="date"
                                    onChange={this.handleInputChange}
                                />
                            </div>
                            <div className="form-group">
                                <RadioButton
                                    label="Has the child had any previous neurodevelopmental support?"
                                    name="previousNdSupport"
                                    options={yesNoUnknownOptions}
                                    value={previousNdSupport}
                                    required={true}
                                    onChange={this.handleInputChange}
                                />
                                {this.validator.message("previous neurodevelopmental support", previousNdSupport, "required")}
                            </div>
                            {previousNdSupport === "Yes" && (
                                <div className="form-group">
                                    <TextArea
                                        name="previousNdSupportDetails"
                                        label="If yes, please give details"
                                        value={previousNdSupportDetails}
                                        required={true}
                                        maxLength={800}
                                        onChange={this.handleInputChange}
                                    />
                                    {this.validator.message("previous support details", previousNdSupportDetails, "required")}
                                </div>
                            )}

                            <h2>6. Reason for referral</h2>
                            <div className="form-group">
                                <TextArea
                                    name="referralReason"
                                    label="Reason for referral"
                                    value={referralReason}
                                    required={true}
                                    maxLength={800}
                                    onChange={this.handleInputChange}
                                />
                                {this.validator.message("reason for referral", referralReason, "required")}
                            </div>
                            <div className="form-group">
                                <TextArea
                                    name="mainConcerns"
                                    label="What are the main concerns or difficulties currently experienced by the child?"
                                    helpText="You may wish to think about communication, sensory processing, self regulation, social interaction and emotional wellbeing."
                                    value={mainConcerns}
                                    required={true}
                                    maxLength={800}
                                    onChange={this.handleInputChange}
                                />
                                {this.validator.message("main concerns", mainConcerns, "required")}
                            </div>
                            <div className="form-group">
                                <CheckBox
                                    label="Which areas are the main focus of concern? (tick all that apply)"
                                    name="focusAreas"
                                    options={focusAreaOptions}
                                    selectedOptions={focusAreas}
                                    onChange={this.handleCheckBox}
                                    required={true}
                                />
                                {this.validator.message("focus of concern", focusAreas, "required")}
                            </div>
                            {(focusAreas || []).indexOf("Other") > -1 && (
                                <div className="form-group">
                                    <Input
                                        inputtype={"text"}
                                        label="Please describe the other area of concern"
                                        name="focusAreaOther"
                                        value={focusAreaOther}
                                        required={true}
                                        onChange={this.handleInputChange}
                                    />{" "}
                                    {this.validator.message("other area of concern", focusAreaOther, "required")}
                                </div>
                            )}

                            <h2>7. Supporting documents</h2>
                            <div className="form-group">
                                <FileInput
                                    name="documents"
                                    label="Upload any supporting documents"
                                    helpText="Up to 10 files, 20MB each. Accepted types: PDF, Word (doc / docx), RTF, JPG and PNG."
                                    files={documents}
                                    onChange={this.handleDocumentsChange}
                                />
                            </div>

                            <h2>8. Risk and safeguarding</h2>
                            <div className="form-group">
                                <RadioButton
                                    label="Current risk status"
                                    name="riskStatus"
                                    options={riskStatusOptions}
                                    value={riskStatus}
                                    required={true}
                                    onChange={this.handleInputChange}
                                />
                                {this.validator.message("current risk status", riskStatus, "required")}
                            </div>
                            {(riskStatus === "Medium" || riskStatus === "High") && (
                                <div className="form-group">
                                    <TextArea
                                        name="riskDetails"
                                        label="If medium or high, please give details"
                                        value={riskDetails}
                                        required={true}
                                        maxLength={800}
                                        onChange={this.handleInputChange}
                                    />
                                    {this.validator.message("risk details", riskDetails, "required")}
                                </div>
                            )}
                            <div className="form-group">
                                <RadioButton
                                    label="Are there any child protection concerns, or is the child on the child protection register?"
                                    name="childProtectionConcerns"
                                    options={yesNoUnknownOptions}
                                    value={childProtectionConcerns}
                                    required={true}
                                    onChange={this.handleInputChange}
                                />
                                {this.validator.message("child protection concerns", childProtectionConcerns, "required")}
                            </div>
                            <div className="form-group">
                                <RadioButton
                                    label="Are there any trauma or adverse experiences to note?"
                                    name="traumaExperiences"
                                    options={yesNoUnknownOptions}
                                    value={traumaExperiences}
                                    required={true}
                                    onChange={this.handleInputChange}
                                />
                                {this.validator.message("trauma or adverse experiences", traumaExperiences, "required")}
                            </div>
                            {traumaExperiences === "Yes" && (
                                <div className="form-group">
                                    <TextArea
                                        name="traumaDetails"
                                        label="If yes, please give details"
                                        value={traumaDetails}
                                        required={true}
                                        maxLength={800}
                                        onChange={this.handleInputChange}
                                    />
                                    {this.validator.message("trauma details", traumaDetails, "required")}
                                </div>
                            )}

                            <h2>9. Consent</h2>
                            <div className="form-group">
                                <RadioButton
                                    label="The parent or carer consents to this referral"
                                    name="consentReferral"
                                    options={yesNoOptions}
                                    value={consentReferral}
                                    required={true}
                                    onChange={this.handleInputChange}
                                />
                                {this.validator.message("consent to referral", consentReferral, "required")}
                            </div>
                            <div className="form-group">
                                <RadioButton
                                    label="The parent or carer consents to NeuroPathways contacting the child's school and sharing information"
                                    name="consentSchoolContact"
                                    options={yesNoOptions}
                                    value={consentSchoolContact}
                                    required={true}
                                    onChange={this.handleInputChange}
                                />
                                {this.validator.message("consent to school contact", consentSchoolContact, "required")}
                            </div>
                            <div className="form-group">
                                <RadioButton
                                    label="The parent or carer consents to anonymised information being used for service reporting"
                                    name="consentAnonymisedReporting"
                                    options={yesNoOptions}
                                    value={consentAnonymisedReporting}
                                    required={true}
                                    onChange={this.handleInputChange}
                                />
                                {this.validator.message("consent to anonymised reporting", consentAnonymisedReporting, "required")}
                            </div>
                            <div className="form-group">
                                <RadioButton
                                    label="Have the child's views about this referral been sought?"
                                    name="childViewsSought"
                                    options={yesNoOptions}
                                    value={childViewsSought}
                                    required={true}
                                    onChange={this.handleInputChange}
                                />
                                {this.validator.message("child's views sought", childViewsSought, "required")}
                            </div>
                            <div className="form-group">
                                <CheckBox
                                    label="
                    This statement is for those who are accessing our services for themselves
                    or on behalf of another person (i.e. making a referral). To process this referral
                    and put in place neurodevelopmental support, the organisation requires the information
                    which is to be entered into the referral form. This is personal information covered under
                    the GDPR regulations and therefore it is a requirement that the person who is being referred
                    (or their parent or carer) has consented to the personal information being shared with this
                    organisation. The data is confidential and not shared with others, but we retain the right to
                    use this information to contact others if we believe the person receiving our support (or others)
                    is in danger or at risk of abuse. As far as possible we will seek consent before taking this action,
                    but the safety of those we support is our priority.
                "
                                    helpText="The person making the referral must confirm that the consent of the
                    parent or carer has been sought and received."
                                    name="neuroPathwaysConfirmation"
                                    options={["I agree"]}
                                    selectedOptions={neuroPathwaysConfirmation}
                                    onChange={this.handleCheckBox}
                                    required={true}
                                />
                                {this.validator.message("agreement", neuroPathwaysConfirmation, "required")}
                            </div>
                            {this.renderSubmitButton()}
                        </form>
                    )}
                    <ResearchDisclaimer />
                </div>
            </div>
        );
    }
}

export default FormContainer;
